/* eslint-disable no-console */
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  AdaButton,
  Icon,
  Loading,
  Modal,
  useBoolean,
} from '@betrybe/ada-react';
import { Card, Footer, Header } from '@/components/organisms';
import useLazyTrack from '@/hooks/tracking/useLazyTrack';
import { ActorTypeEnum, SourceEnum, VerbEnum } from '@/tracker/enums';
import { useAuthentication, useClient } from '@/hooks';
import { detectBrowser } from '@/utils';
import { GetClientData, useLogout } from '../utils';

export type DefaultLayoutProps = {
  step?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  titleAlign?: 'left' | 'right' | 'center';
  cardSize?: 'big-card' | 'normal-card' | 'large-card';
  progressPercentage?: number;
  limitDate?: string;
};

export default function DefaultLayout({
  step,
  children,
  title,
  description,
  titleAlign,
  cardSize,
  progressPercentage,
  limitDate,
}: DefaultLayoutProps): ReactElement {
  const client = useClient();
  const { token } = useAuthentication();
  const track = useLazyTrack();
  const [modalOpened, { setTrue: openModal, setFalse: closeModal }] =
    useBoolean(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { logout } = useLogout();

  useEffect(() => {
    if (detectBrowser() === 'Safari') {
      openModal();
    }
  }, [openModal]);

  useEffect(() => {
    if (window.location.pathname === '/') return;

    if (window.location.href.includes('#access_token')) return;

    track(
      'Page viewed',
      {
        actor: {
          user_id: client.clientId,
          type: ActorTypeEnum.CANDIDATE,
          role: '',
        },
        object: {
          path: window.location.pathname,
          url: window.location.href,
          process_info: client.processInfo,
        },
        context: {
          source: SourceEnum.FINANCING_REQUEST,
        },
        verb: VerbEnum.VIEW,
      },
      'page',
    );
  }, [client.clientId, client.processInfo, track]);

  const callModalTrack = () => {
    track(
      'Button clicked',
      {
        actor: {
          user_id: '',
          type: ActorTypeEnum.CANDIDATE,
          role: '',
        },
        object: {
          path: window.location.pathname,
          url: window.location.href,
          component: 'Ainda não funcionamos no Safari',
        },
        context: {
          source: SourceEnum.FINANCING_REQUEST,
        },
        verb: VerbEnum.VIEW,
      },
      'track',
    );
  };

  return (
    <div className="layout-container">
      <GetClientData />
      <Header />
      {loading ? (
        <Loading>Aguarde...</Loading>
      ) : (
        <>
          <div className={`layout-container__${cardSize}`}>
            <Card
              description={description}
              title={title}
              step={step}
              titleAlign={titleAlign}
              cardSize={cardSize}
              progressPercentage={progressPercentage}
              limitDate={limitDate}
            >
              {children}
            </Card>
          </div>

          {token && client.clientId && (
            <div
              className="layout-container__logout-link"
              onClick={() => logout(setLoading)}
              onKeyPress={() => logout(setLoading)}
              role="link"
              tabIndex={0}
            >
              <Icon name="input" height={24} />
              <p>Sair</p>
            </div>
          )}
        </>
      )}

      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          callModalTrack();
        }}
        actions={
          <div>
            <AdaButton
              kind="primary"
              onClick={() => {
                closeModal();
                callModalTrack();
              }}
              type="button"
            >
              Confirmar
            </AdaButton>
          </div>
        }
        title="Ainda não funcionamos no Safari 😕"
      >
        <p>Por favor, tente novamente em um navegador diferente.</p>
      </Modal>
      <Footer />
    </div>
  );
}
